import { authedFetch } from "@/lib/authed-fetch";
import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Upload, Trash2, Play, Volume2, AlertCircle, Shield, Bell, AlertTriangle, RefreshCw, Phone } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { SoundSelector } from "./sound-selector";
import type { NotificationSound } from "@shared/schema";

export function NotificationSoundsSettings() {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [soundName, setSoundName] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [playingId, setPlayingId] = useState<number | null>(null);
  const [brokenSounds, setBrokenSounds] = useState<Set<number>>(new Set());
  const [checkingStatus, setCheckingStatus] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const { data: sounds = [], isLoading } = useQuery<NotificationSound[]>({
    queryKey: ['/api/notification-sounds'],
  });

  // Check which sounds are broken (can't be loaded)
  useEffect(() => {
    if (sounds.length === 0) return;
    
    const checkSounds = async () => {
      const broken = new Set<number>();
      
      for (const sound of sounds) {
        if (!sound.directUrl) {
          broken.add(sound.id);
          continue;
        }
        
        // Check if the sound URL is local (won't work in production)
        if (sound.directUrl.startsWith('/uploads/')) {
          // Try to fetch the file to see if it exists
          try {
            const response = await fetch(sound.directUrl, { method: 'HEAD' });
            if (!response.ok) {
              broken.add(sound.id);
            }
          } catch {
            broken.add(sound.id);
          }
        }
      }
      
      setBrokenSounds(broken);
    };
    
    checkSounds();
  }, [sounds]);

  const { data: countData } = useQuery<{ count: number; max: number }>({
    queryKey: ['/api/notification-sounds/count'],
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest('DELETE', `/api/notification-sounds/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/notification-sounds'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notification-sounds/count'] });
      toast({
        title: "Sound deleted",
        description: "Notification sound has been removed",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete sound",
        variant: "destructive",
      });
    },
  });

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('audio/')) {
      toast({
        title: "Invalid file",
        description: "Please select an audio file (WAV, MP3, etc.)",
        variant: "destructive",
      });
      return;
    }

    // Check duration using Web Audio API
    const audioContext = new AudioContext();
    const arrayBuffer = await file.arrayBuffer();
    
    try {
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      const durationMs = Math.round(audioBuffer.duration * 1000);
      
      if (durationMs > 4000) {
        toast({
          title: "File too long",
          description: `Audio must be 4 seconds or less. This file is ${(durationMs / 1000).toFixed(1)} seconds.`,
          variant: "destructive",
        });
        audioContext.close();
        return;
      }

      // Upload file
      setIsUploading(true);
      
      const formData = new FormData();
      formData.append('audio', file);
      formData.append('name', soundName || file.name.replace(/\.[^/.]+$/, ''));
      formData.append('durationMs', durationMs.toString());

      const response = await authedFetch('/api/notification-sounds', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Upload failed');
      }

      queryClient.invalidateQueries({ queryKey: ['/api/notification-sounds'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notification-sounds/count'] });
      
      toast({
        title: "Sound uploaded",
        description: "Notification sound has been added",
      });
      
      setSoundName("");
      audioContext.close();
    } catch (error: any) {
      toast({
        title: "Upload failed",
        description: error.message || "Failed to upload audio file",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const playPreview = (sound: NotificationSound) => {
    if (!sound.directUrl) {
      toast({
        title: "Cannot play",
        description: "No playable URL for this sound",
        variant: "destructive",
      });
      return;
    }

    // Stop any currently playing
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    if (playingId === sound.id) {
      setPlayingId(null);
      return;
    }

    const audio = new Audio(sound.directUrl);
    audioRef.current = audio;
    setPlayingId(sound.id);
    
    audio.onended = () => {
      setPlayingId(null);
      audioRef.current = null;
    };
    
    audio.onerror = () => {
      setPlayingId(null);
      audioRef.current = null;
      toast({
        title: "Playback failed",
        description: "Could not play this sound",
        variant: "destructive",
      });
    };
    
    audio.play();
  };

  const canUpload = (countData?.count ?? 0) < (countData?.max ?? 10);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-white">Notification Sounds</h3>
          <p className="text-sm text-gray-400">
            Upload audio files for notification alerts. Max 10 sounds, 4 seconds each.
          </p>
        </div>
        <div className="text-sm text-gray-400">
          {countData?.count ?? 0} / {countData?.max ?? 10} sounds
        </div>
      </div>

      {/* Upload Section */}
      {canUpload ? (
        <div className="flex items-end gap-4 p-4 border border-gray-600 rounded-lg bg-gray-800/50">
          <div className="flex-1">
            <Label htmlFor="sound-name" className="text-gray-300">Sound Name (optional)</Label>
            <Input
              id="sound-name"
              value={soundName}
              onChange={(e) => setSoundName(e.target.value)}
              placeholder="e.g., Chime, Alert, Ding"
              className="mt-1 bg-gray-700 border-gray-600 text-white"
              data-testid="input-sound-name"
            />
          </div>
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="input-sound-file"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="button-upload-sound"
            >
              {isUploading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              Upload Sound
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2 p-4 border border-yellow-600/50 rounded-lg bg-yellow-900/20">
          <AlertCircle className="h-5 w-5 text-yellow-500" />
          <span className="text-yellow-400">Maximum of 10 sounds reached. Delete one to upload more.</span>
        </div>
      )}

      {/* Sound List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : sounds.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <Volume2 className="h-12 w-12 mx-auto mb-2 opacity-50" />
          <p>No notification sounds uploaded yet</p>
          <p className="text-sm">Upload WAV or MP3 files to use as notification alerts</p>
        </div>
      ) : (
        <div className="space-y-2">
          {sounds.map((sound) => {
            const isBroken = brokenSounds.has(sound.id);
            const isLocalFile = sound.directUrl?.startsWith('/uploads/');
            
            return (
              <div
                key={sound.id}
                className={`flex items-center justify-between p-3 border rounded-lg hover:bg-gray-800/50 ${
                  isBroken 
                    ? 'border-red-600/50 bg-red-900/20' 
                    : 'border-gray-600 bg-gray-800/30'
                }`}
                data-testid={`sound-item-${sound.id}`}
              >
                <div className="flex items-center gap-3">
                  {isBroken ? (
                    <div className="h-8 w-8 flex items-center justify-center">
                      <AlertTriangle className="h-4 w-4 text-red-400" />
                    </div>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => playPreview(sound)}
                      className={`h-8 w-8 p-0 ${playingId === sound.id ? 'text-blue-400' : 'text-gray-400 hover:text-white'}`}
                      data-testid={`button-play-${sound.id}`}
                    >
                      <Play className={`h-4 w-4 ${playingId === sound.id ? 'animate-pulse' : ''}`} />
                    </Button>
                  )}
                  <div>
                    <p className={`font-medium ${isBroken ? 'text-red-300' : 'text-white'}`}>
                      {sound.name}
                      {isBroken && <span className="ml-2 text-xs text-red-400">(File missing)</span>}
                    </p>
                    <p className="text-xs text-gray-400">
                      {sound.durationMs ? `${(sound.durationMs / 1000).toFixed(1)}s` : 'Duration unknown'}
                      {isLocalFile && !isBroken && <span className="ml-2 text-yellow-500">(Local - will be lost on redeploy)</span>}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => deleteMutation.mutate(sound.id)}
                  disabled={deleteMutation.isPending}
                  className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                  data-testid={`button-delete-${sound.id}`}
                >
                  {deleteMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {/* Active Calls chime — per-user; separate from the incoming/outgoing
          call ring (those only play when you are personally called/calling). */}
      <div className="mt-8 pt-6 border-t border-gray-600">
        <h4 className="text-md font-medium text-white mb-4">Active Calls</h4>
        <p className="text-sm text-gray-400 mb-4">
          Plays once when a new call appears in your Active Calls bar (calls on your projects — all calls for admins). Does not replace the ring for calls directed at you.
        </p>
        <div className="flex items-center justify-between p-3 border border-gray-600 rounded-lg bg-gray-800/30">
          <div className="flex items-center gap-3">
            <Phone className="h-5 w-5 text-emerald-500" />
            <div>
              <p className="text-white font-medium">New active call</p>
              <p className="text-xs text-gray-400">A related call just started somewhere</p>
            </div>
          </div>
          <SoundSelector soundType="soundActiveCalls" label="Select Sound" />
        </div>
      </div>

      {/* Scheduled callback chime — independent from Active Calls so a user
          can distinguish a new callback request from a call already in progress. */}
      <div className="mt-8 pt-6 border-t border-gray-600">
        <h4 className="text-md font-medium text-white mb-4">Scheduled Calls</h4>
        <p className="text-sm text-gray-400 mb-4">
          Plays once when a new callback request appears in the Scheduled section.
        </p>
        <div className="flex items-center justify-between p-3 border border-gray-600 rounded-lg bg-gray-800/30">
          <div className="flex items-center gap-3">
            <Phone className="h-5 w-5 text-amber-400" />
            <div>
              <p className="text-white font-medium">New scheduled call</p>
              <p className="text-xs text-gray-400">A callback request just arrived</p>
            </div>
          </div>
          <SoundSelector soundType="soundScheduledCalls" label="Select Sound" />
        </div>
      </div>

      {/* Admin Sound Preferences */}
      <div className="mt-8 pt-6 border-t border-gray-600">
        <h4 className="text-md font-medium text-white mb-4">Admin Sound Preferences</h4>
        <p className="text-sm text-gray-400 mb-4">
          Configure sounds for admin-only notification types.
        </p>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 border border-gray-600 rounded-lg bg-gray-800/30">
            <div className="flex items-center gap-3">
              <Bell className="h-5 w-5 text-yellow-500" />
              <div>
                <p className="text-white font-medium">Admin Notifications</p>
                <p className="text-xs text-gray-400">System alerts for administrators</p>
              </div>
            </div>
            <SoundSelector soundType="soundAdminNotifications" label="Select Sound" />
          </div>

          <div className="flex items-center justify-between p-3 border border-gray-600 rounded-lg bg-gray-800/30">
            <div className="flex items-center gap-3">
              <Shield className="h-5 w-5 text-red-500" />
              <div>
                <p className="text-white font-medium">Security Notifications</p>
                <p className="text-xs text-gray-400">Login attempts, password changes, etc.</p>
              </div>
            </div>
            <SoundSelector soundType="soundSecurityNotifications" label="Select Sound" />
          </div>
        </div>
      </div>
    </div>
  );
}
